<template>
    <Teleport to="body">
        <div
            :id="id"
            ref="tooltipRef"
            role="tooltip"
            class="ds-month-tooltip"
            :style="{ left: `${position.left}px`, top: `${position.top}px`, visibility: position.ready ? 'visible' : 'hidden' }"
        >
            <!-- День и дата — одной строкой, должность всегда строкой ниже. -->
            <div class="ds-month-tooltip-header flex flex-col items-start gap-0.5">
                <span class="ds-month-tooltip-title whitespace-nowrap">{{ title }}</span>
                <span v-if="!showShiftLabels && shifts[0]" class="ds-month-tooltip-position">{{ shifts[0].positionName }}</span>
            </div>

            <!-- RD-185: часы КС — справа от полного названия КС, в колонке часов смен; в «Итого» не входят. -->
            <div v-if="dayState" class="ds-month-tooltip-day-state" :class="{ 'ds-month-tooltip-day-state-with-hours': dayState.duration }">
                <span class="ds-month-tooltip-day-state-name" :style="{ backgroundColor: dayState.color }">{{ dayState.name }}</span>
                <span v-if="dayState.duration" class="ds-month-tooltip-hours" data-day-state-duration>{{ dayState.duration }}</span>
            </div>

            <template v-for="(shift, index) in shifts" :key="shift.key">
                <div v-if="index > 0 || dayState" class="ds-month-tooltip-divider" />
                <div class="ds-month-tooltip-shift">
                    <!-- Как в заголовке: метка смены, должность строкой ниже. -->
                    <div v-if="showShiftLabels" class="ds-month-tooltip-shift-label flex flex-col items-start gap-0.5">
                        <span v-if="shift.isExtra" class="ds-month-tooltip-extra">Доп. смена</span>
                        <span v-else class="ds-month-tooltip-main">Основная смена</span>
                        <span class="ds-month-tooltip-position">{{ shift.positionName }}</span>
                    </div>
                    <div class="ds-month-tooltip-rows">
                        <span class="ds-month-tooltip-label">План</span>
                        <span class="ds-month-tooltip-plan">{{ shift.planStart }}–{{ shift.planEnd }}<span v-if="shift.planEndDate" class="ds-month-tooltip-end-date">{{ shift.planEndDate }}</span></span>
                        <span />
                        <span class="ds-month-tooltip-label">Факт</span>
                        <span v-if="hasFactTimes(shift)" class="ds-month-tooltip-fact">
                            <span class="ds-month-tooltip-fact-time" :class="{ 'ds-month-tooltip-fact-violation': shift.startViolated }">{{ shift.factStart || '—' }}</span>–<span class="ds-month-tooltip-fact-time" :class="{ 'ds-month-tooltip-fact-violation': shift.endViolated }">{{ shift.factEnd || '—' }}</span><span v-if="shift.factEndDate" class="ds-month-tooltip-end-date">{{ shift.factEndDate }}</span>
                        </span>
                        <span v-else class="ds-month-tooltip-fact ds-month-tooltip-muted">—</span>
                        <span class="ds-month-tooltip-hours" :class="{ 'ds-month-tooltip-muted': shift.isInProgress }">{{ shift.hoursText }}</span>
                    </div>
                    <div v-if="shiftChips(shift).length" class="ds-month-tooltip-chips">
                        <span
                            v-for="chip in shiftChips(shift)"
                            :key="chip.text"
                            class="ds-month-tooltip-chip"
                            :class="chip.kind === 'violation' ? 'ds-month-tooltip-chip-violation' : 'ds-month-tooltip-chip-no-marks'"
                        >{{ chip.text }}</span>
                    </div>
                </div>
            </template>

            <template v-if="total || isNightSingle || hasNoIssues">
                <div class="ds-month-tooltip-divider" />
                <div class="ds-month-tooltip-footer">
                    <span v-if="total" class="ds-month-tooltip-total">Итого {{ total }}</span>
                    <span v-else-if="isNightSingle" class="ds-month-tooltip-note">Ночная смена</span>
                    <span v-else />
                    <span v-if="hasNoIssues" class="ds-month-tooltip-chip ds-month-tooltip-chip-ok">Без нарушений</span>
                </div>
            </template>

            <!-- RD-182: внешняя смена — отдельный раздел; магазин относится только к ней. Факт, часы
                 и нарушения внешней не показываются и в итог собственных смен не входят. -->
            <template v-for="(external, index) in externalShifts" :key="external.key">
                <div v-if="index > 0 || shifts.length || dayState" class="ds-month-tooltip-divider" />
                <div class="ds-month-tooltip-external" data-external-section>
                    <div class="ds-month-tooltip-external-title">
                        <span class="ds-month-tooltip-external-marker" aria-hidden="true" />
                        <span>В другом магазине · <span class="ds-month-tooltip-external-name">{{ external.departmentName }}</span></span>
                    </div>
                    <span v-if="external.positionName" class="ds-month-tooltip-position">{{ external.positionName }}</span>
                    <div class="ds-month-tooltip-rows ds-month-tooltip-rows-external">
                        <span class="ds-month-tooltip-label">План</span>
                        <span :class="external.plan ? 'ds-month-tooltip-plan' : 'ds-month-tooltip-muted'">{{ external.planText }}</span>
                    </div>
                    <span v-if="external.isVacancy" class="ds-month-tooltip-source">С биржи</span>
                </div>
            </template>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import type { ExternalShiftView } from '@/utils/otherDepartmentShifts'
import { placeCellTooltip, type MonthShiftView } from './weekSchedule'

interface Props {
    id: string
    anchor: HTMLElement
    title: string
    shifts: MonthShiftView[]
    // duration — готовые часы КС (RD-185); пусто — не показываются.
    dayState: { name: string, color: string, duration?: string } | null
    externalShifts: ExternalShiftView[]
    total: string
}

type ChipKind = 'violation' | 'no-marks'

const props = defineProps<Props>()

const tooltipRef = ref<HTMLElement | null>(null)
const position = reactive({ left: 0, top: 0, ready: false })

const showShiftLabels = computed(() => props.shifts.length > 1 || Boolean(props.dayState))
const isNightSingle = computed(() => props.shifts.length === 1 && props.shifts[0].planNextDay)
// «Без нарушений» — только для смен, у которых уже есть что оценивать: не у будущих и не у незавершённых —
// есть начало без окончания, в том числе после конца плана (RD-171).
const hasNoIssues = computed(() => (
    props.shifts.some(shift => !shift.isUpcoming)
    && props.shifts.every(shift => !shift.violationCount && !shift.hasGeneralViolation && !shift.isNoMarks && !shift.isFactOpen)
))

const hasFactTimes = (shift: MonthShiftView) => Boolean(
    (shift.factStart && shift.factStart !== '—') || (shift.factEnd && shift.factEnd !== '—'),
)

// Подписи нарушений — существующие сообщения backend (start_msg/end_msg) без собственных порогов.
function shiftChips(shift: MonthShiftView) {
    const chips: { text: string, kind: ChipKind }[] = shift.violationMessages.map(text => ({ text, kind: 'violation' }))
    if ((shift.violationCount || shift.hasGeneralViolation) && !chips.length) chips.push({ text: 'Нарушение', kind: 'violation' })
    if (shift.isNoMarks) chips.push({ text: 'Нет отметок', kind: 'no-marks' })
    return chips
}

// Под ячейкой, при нехватке места — над ней; по горизонтали не выходит за правый край viewport.
function place() {
    const element = tooltipRef.value
    if (!element) return
    Object.assign(position, placeCellTooltip(element, props.anchor), { ready: true })
}

onMounted(place)

// Содержимое может измениться при фоновом обновлении данных — пересчитываем размер и позицию.
watch(() => [props.shifts, props.dayState, props.externalShifts, props.total], () => nextTick(place), { deep: true })
</script>
